import { inject, Injectable, Injector } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Application, ApplicationType } from '@shared/interfaces/application.interface';
import { HttpClient } from '@angular/common/http';
import { ConnectionData } from '@shared/interfaces/connection-data.interface';
import { AppConfigService } from '@app/config/config.service';
import { LocalStorageKeys, RequestHeaderConst } from '@workflow/ecp-shared/constant/information.const';
import { TokenService, SawDust } from '@cudirect/shared-ui-library';
import { AppConfig } from '@app/config/config.type';
import { RuntimeConfigService } from '@app/service/runtime-config.service';

@Injectable({
  providedIn: 'root'
})
export class NewApplicationsService {
  private logger = inject(SawDust).withContext({ component: 'NewApplicationsService' });
  // Subjects
  public applications$ = new Subject<Application[]>();
  public currentApplication$ = new Subject<Application>();
  public currentApplicationId$ = new Subject<string>();
  public currentApplicationObject: Application;
  public apiConfig: AppConfig;
  public currentApplicationIdValue: string;

  constructor(
    private readonly injector: Injector,
    private readonly tokenService: TokenService,
    private readonly runtimeConfigService: RuntimeConfigService
  ) {
    this.apiConfig = this.runtimeConfigService.getConfig as AppConfig;
  }

  /**
   * Observable for getting all applications for the authenticated user.
   */
  /**
   * Subscription to get the Dashboard data, all of the Applications a user has access to
   */
  public getUserApplications(): Observable<Application[]> {
    return this.applications$.asObservable();
  }

  /**
   * This will get an application for the Post App/Overview
   * The loan summaries call is identical to the LoanSummary call, but the loan summaries call returns all applications
   * Where the Applications call only returns exactly one application
   * Observable for getting the current application for the authenticated user.
   * This is the application that is currently being worked on or view by the user.
   */
  public get currentApplication(): Observable<Application> {
    return this.currentApplication$.asObservable();
  }

  /**
   * This can be used only for the post application
   * An in-progress bundle needs a completely differnt contract than a submitted bundle.
   * Method to set the current application
   */
  public setCurrentApplication(application: Application): void {
    this.currentApplicationObject = application;
    this.currentApplication$.next(application);
  }

  public setcurrentApplicationId(id: string): void {
    this.currentApplicationIdValue = id;
    this.currentApplicationId$.next(id);
  }

  /**
   * Method to clear the current application observable
   */
  public clearCurrentApplication(): void {
    this.currentApplication$.next(null);
    this.currentApplicationId$.next(null);
  }

  public get currentApplicationId(): Observable<string> {
    return this.currentApplicationId$.asObservable();
  }

  /**
   * getApps - get all applications for the authenticated user
   * @name - string, name of the application part
   */
  public async getApps(): Promise<void> {
    const http = this.injector.get(HttpClient);
    const routeName = '/loan/appsummaries';
    const settings: ConnectionData = this.constructAPISettings(routeName, this.apiConfig);
    // Whether to use a JWT or not. If not, will default to a session ID in Authorization header.
    const useJWT = false;
    const headers = this.appendTokenToHeader(settings.headers, useJWT);

    const asyncResponse = await http
      .get(settings.url, { headers })
      .toPromise()
      .then((data: Application[]) => {
        // Success state
        const applications = data;
        this.applications$.next(applications);
      })
      .catch((e) => {
        this.logger.withError(e).error('Error getting applications');
        // Error state
      });
    return asyncResponse;
  }

  /**
   * Method to clear the applications observable
   */
  public clearApplications(): void {
    this.applications$.next([]);
  }

  /**
   * Method to clear the applications observable
   */
  public createNewApplication(applicationType: ApplicationType): void {
    if (applicationType === 'Membership') {
      this.logger.info('Membership');
      // this.consumerService.startNewApplication();
    } else {
      this.logger.info('Credit');
      // this.loanService.createOLALoan()
    }
  }

  /**
   * Function to grab API settings from JSON file via config service
   * @param name - the name of the api config object from the JSON
   */
  private constructAPISettings = (routeName: string, apiConfig): ConnectionData => {
    let settings: ConnectionData = {
      url: ''
    };

    if (!apiConfig) {
      return settings;
    }

    const baseUrl = this.runtimeConfigService.getDXAPIBaseUrl();
    const url = `${baseUrl + routeName}`;
    const headers = apiConfig?.consumerPortalApi?.headers;
    const version = '1.0';
    headers['x-api-version'] = version;

    settings = {
      url,
      headers
    };

    return settings;
  };

  /**
   * Appends the bearer token to the authorization in the header
   * This will give us the flexibility to use JWT or Session ID in the Authorization header
   * @param headers - UEM data headers
   * @returns headers object
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly appendTokenToHeader = (headers, useJWT): any => {
    const userJWT = this.tokenService.getCachedToken();
    const UserObj = JSON.parse(sessionStorage.getItem(LocalStorageKeys.userStorageName));

    if (useJWT) {
      return userJWT ? { ...headers, [RequestHeaderConst.authorization]: 'Bearer ' + userJWT?.access_token } : headers;
    } else {
      return { ...headers, [RequestHeaderConst.authorization]: UserObj?.token, [RequestHeaderConst.dxSessionId]: UserObj?.token };
    }
  };
}
